import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiClient } from '../api/helpers.ts'
import { env } from '../env.ts'
import { getParentDeviceMap, useDevices } from './devices.ts'
import { useImpersonation } from './impersonation.ts'
import { type ExpandedMeasurement, useLiveMeasurements } from './liveMeasurements.ts'

const measurementAlertsApi = createApiClient({ prefixUrl: env.API_MEASUREMENT_ALERTS })

export const measurementAlertQueryOptions = (partyAccountId?: string) => ({
	queryKey: ['MEASUREMENT_ALERTS', partyAccountId],
	queryFn: () => {
		const searchParams = new URLSearchParams()
		if (partyAccountId) searchParams.append('partyAccountId', partyAccountId)
		return measurementAlertsApi.get('config', { searchParams }).json<MeasurementAlertConfiguration[]>()
	},
	staleTime: 1000 * 60 * 10,
})

export const useMeasurementAlerts = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery(measurementAlertQueryOptions(partyAccountId))
}

export const useCreateMeasurementAlert = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateMeasurementAlertRequest) => {
			const searchParams = new URLSearchParams()
			if (partyAccountId) searchParams.append('partyAccountId', partyAccountId)
			return measurementAlertsApi.post('config', { json: data, searchParams })
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['MEASUREMENT_ALERTS', partyAccountId] })
		},
	})
}

export const useUpdateMeasurementAlert = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: UpdateMeasurementAlertRequest) => {
			const searchParams = new URLSearchParams()
			if (partyAccountId) searchParams.append('partyAccountId', partyAccountId)
			return measurementAlertsApi.patch(`config/${data.id}`, { json: data, searchParams })
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['MEASUREMENT_ALERTS', partyAccountId] })
		},
	})
}

export const useDeleteMeasurementAlert = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (id: string) => {
			const searchParams = new URLSearchParams()
			if (partyAccountId) searchParams.append('partyAccountId', partyAccountId)
			return measurementAlertsApi.delete(`config/${id}`, { searchParams })
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['MEASUREMENT_ALERTS', partyAccountId] })
		},
	})
}

export type MeasurementAlertConfiguration = {
	type: 'MEASUREMENT_ALERT_RULE'
	status: 'ACTIVE' | 'INACTIVE' | 'DELETED'
	id: string
	name: string
	context: {
		sourceId: string
	}
	userGuidelines: string
	simpleRules: {
		parameterName: string
		statistic?: string
		period?: string
		height?: number
		threshold:
			| {
					minValue: number
			  }
			| {
					maxValue: number
			  }
	}[]
	description?: string
	duration?: string
	notification?: {
		partyAccountId: string
		contactListId: string | string[]
		productId: string
	}
}

export type CreateMeasurementAlertRequest = Pick<
	MeasurementAlertConfiguration,
	'name' | 'context' | 'userGuidelines' | 'simpleRules' | 'description' | 'duration' | 'notification'
>

export type UpdateMeasurementAlertRequest = Omit<MeasurementAlertConfiguration, 'notification'> & {
	notification?: MeasurementAlertConfiguration['notification'] | null
}

export const getActiveMeasurementAlerts = (
	alertConfigurations: MeasurementAlertConfiguration[],
	allMeasurements: ExpandedMeasurement[] | undefined,
) => {
	if (!alertConfigurations || !allMeasurements) {
		return []
	}

	const isMeasurementTypeMatch = (measurement: ExpandedMeasurement, simpleRule: MeasurementAlertConfiguration['simpleRules'][0]) => {
		const { measurementType } = measurement
		return !(
			(simpleRule.height !== undefined && measurementType.height !== simpleRule.height) ||
			(simpleRule.period !== undefined && measurementType.period !== simpleRule.period) ||
			(simpleRule.statistic !== undefined && measurementType.statistic !== simpleRule.statistic)
		)
	}

	const isThresholdExceeded = (value: number, threshold: MeasurementAlertConfiguration['simpleRules'][0]['threshold']) => {
		return ('maxValue' in threshold && value > threshold.maxValue) || ('minValue' in threshold && value < threshold.minValue)
	}

	const processMeasurement = (measurement: ExpandedMeasurement, config: MeasurementAlertConfiguration) => {
		const simpleRule = config.simpleRules.find((threshold) => threshold.parameterName === measurement.measurementType.parameter)

		if (!simpleRule || !isMeasurementTypeMatch(measurement, simpleRule)) {
			return undefined
		}

		if (isThresholdExceeded(measurement.value, simpleRule.threshold)) {
			return {
				rule: simpleRule,
				value: measurement.value,
				observedTime: measurement.observedTime,
				id: config.id,
			}
		}

		return undefined
	}

	const activeAlerts = alertConfigurations.flatMap((config) => {
		const measurements = allMeasurements.filter((measurement) => measurement.sourceId === config.context.sourceId)

		return measurements.map((measurement) => processMeasurement(measurement, config)).filter((alert) => alert !== undefined)
	})
	return activeAlerts
}

export const useMeasurementAlertsRecord = () => {
	const { data: alertConfigurations = [] } = useMeasurementAlerts()
	const { data: devices } = useDevices()
	const { data: measurements } = useLiveMeasurements()
	const parentDeviceMap = getParentDeviceMap(devices)

	const alertRecord = devices.reduce(
		(acc, cur) => {
			acc[cur.id] = { active: [], inactive: [] }
			return acc
		},
		{} as Record<
			string,
			{
				active: { config: MeasurementAlertConfiguration; value: number; observedTime: string }[]
				inactive: { config: MeasurementAlertConfiguration }[]
			}
		>,
	)

	alertConfigurations.forEach((config) => {
		const parentDeviceId = parentDeviceMap.get(config.context.sourceId)
		if (!parentDeviceId) return

		const sensorMeasurements = Object.values(measurements[config.context.sourceId] ?? {})
		const activeAlerts = getActiveMeasurementAlerts([config], sensorMeasurements)
		if (activeAlerts.length > 0) {
			for (const activeAlert of activeAlerts) {
				alertRecord[parentDeviceId]?.active.push({ config, value: activeAlert.value, observedTime: activeAlert.observedTime })
			}
		} else {
			alertRecord[parentDeviceId]?.inactive.push({ config })
		}
	})
	return alertRecord
}
